import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chip,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Tooltip,
  Typography,
  Theme,
  createStyles,
  makeStyles,
} from '@bb-ui/react-library';
import { OpenInNewWindow } from '@bb-ui/icons/dist/small';
import { Alert } from '@bb-ui/icons/dist/large';
import {
  AwsAsgIcon,
  AwsEc2Icon,
  AwsInstallerIcon,
  AwsRdsIcon,
  AwsRegionIcon,
} from './AwsServiceIcons';
import { RefreshButton } from 'components/RefreshButton/RefreshButton';
import { InventoryAsg, InventoryEc2Instance, InventoryRdsInstance } from './types';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ErrorMessage } from 'components/ErrorMessage/ErrorMessage';
import { useTenantContext } from 'contexts/TenantContext';
import { useLearnSiteContext } from 'contexts/LearnSiteContext/LearnSiteContext';
import { useLearnInstance } from 'hooks/useLearnInstance';
import { TenantInformationItem } from '../TenantInformation/TenantInformationItem';
import useTenantInventory from './useTenantInventory';
import { asgUrl, ec2InstanceUrl, rdsUrl } from './awsConsoleUrls';
import { AwsLogo } from './AwsLogo';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pageTitleRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
    },
    pageTitleLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    accountChip: {
      backgroundColor: theme.palette.grey[100],
      fontFamily: 'Menlo, Consolas, monospace',
    },
    typeChip: {
      height: 22,
      backgroundColor: theme.palette.grey[100],
      fontFamily: 'Menlo, Consolas, monospace',
      fontWeight: theme.typography.fontWeightBold,
    },
    instanceGroup: {
      marginBottom: theme.spacing(2),
    },
    instanceGroupHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
      paddingLeft: theme.spacing(0.5),
    },
    instanceGroupName: {
      fontWeight: theme.typography.fontWeightBold,
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      letterSpacing: '0.02em',
    },
    sectionTitle: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    sectionTitleIcon: {
      color: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
    },
    sectionCountChip: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      fontWeight: theme.typography.fontWeightBold,
      marginLeft: theme.spacing(0.5),
      height: 22,
      minWidth: 28,
    },
    panel: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      transition: 'box-shadow 200ms ease',
      '&:hover': {
        boxShadow: theme.shadows[2],
      },
    },
    panelDetails: {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    summary: {
      gap: theme.spacing(1),
    },
    summaryRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      width: '100%',
    },
    summaryLabel: {
      fontWeight: theme.typography.fontWeightMedium,
      flexShrink: 0,
    },
    awsChip: {
      marginLeft: 'auto',
    },
    stateChip: {
      height: 22,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: 'capitalize',
      letterSpacing: '0.02em',
    },
    stateChipRunning: {
      backgroundColor: '#1B873F',
      color: '#fff',
    },
    stateChipPending: {
      backgroundColor: '#C77700',
      color: '#fff',
    },
    stateChipStopped: {
      backgroundColor: '#C92A2A',
      color: '#fff',
    },
    stateChipUnknown: {
      backgroundColor: theme.palette.grey[600],
      color: '#fff',
    },
    roleChip: {
      height: 22,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: 'capitalize',
      letterSpacing: '0.02em',
    },
    roleChipPrimary: {
      backgroundColor: '#205493',
      color: '#fff',
    },
    roleChipReplica: {
      backgroundColor: '#6F42C1',
      color: '#fff',
    },
    emptySection: {
      color: theme.palette.text.disabled,
      fontStyle: 'italic',
      marginBottom: theme.spacing(2),
    },
    refreshBlurWrapper: {
      transition: 'filter 200ms ease, opacity 200ms ease',
    },
    refreshBlurActive: {
      filter: 'blur(2px)',
      opacity: 0.55,
      pointerEvents: 'none',
      userSelect: 'none',
    },
    unavailableWrapper: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      paddingTop: theme.spacing(6),
      paddingBottom: theme.spacing(6),
    },
    unavailableCard: {
      width: '100%',
      maxWidth: 560,
      padding: theme.spacing(4),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[1],
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: theme.spacing(2),
    },
    unavailableIcon: {
      color: theme.palette.warning.main,
      marginBottom: theme.spacing(1),
    },
    unavailableTitle: {
      fontWeight: theme.typography.fontWeightBold,
    },
    unavailableStatusRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    unavailableHelpList: {
      margin: 0,
      paddingLeft: theme.spacing(3),
      textAlign: 'left',
      color: theme.palette.text.secondary,
    },
  }),
);

interface SectionProps {
  sectionKey: 'asg' | 'installer' | 'instances' | 'rds';
  title: string;
  emptyLabel: string;
  isEmpty: boolean;
  count?: number;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  sectionKey,
  title,
  emptyLabel,
  isEmpty,
  count,
  icon,
  children,
}) => {
  const classes = useStyles();
  return (
    <>
      <Typography className={classes.sectionTitle} variant="h3" component="h3">
        {icon && <span className={classes.sectionTitleIcon}>{icon}</span>}
        {title}
        {typeof count === 'number' && count > 0 && (
          <Chip className={classes.sectionCountChip} label={count} size="small" />
        )}
      </Typography>
      {isEmpty ? (
        <Typography className={classes.emptySection} data-testid={`inventory-empty-${sectionKey}`}>
          {emptyLabel}
        </Typography>
      ) : (
        children
      )}
    </>
  );
};

function stateChipClass(state: string | undefined, classes: ReturnType<typeof useStyles>): string {
  const normalized = (state || '').toLowerCase();
  if (normalized === 'running' || normalized === 'available' || normalized === 'in-service') {
    return classes.stateChipRunning;
  }
  if (
    normalized === 'pending' ||
    normalized === 'modifying' ||
    normalized === 'backing-up' ||
    normalized === 'stopping' ||
    normalized === 'starting' ||
    normalized === 'rebooting'
  ) {
    return classes.stateChipPending;
  }
  if (
    normalized === 'stopped' ||
    normalized === 'terminated' ||
    normalized === 'shutting-down' ||
    normalized === 'failed'
  ) {
    return classes.stateChipStopped;
  }
  return classes.stateChipUnknown;
}

const StateChip: React.FC<{ state?: string }> = ({ state }) => {
  const classes = useStyles();
  if (!state) return null;
  return (
    <Chip
      className={`${classes.stateChip} ${stateChipClass(state, classes)}`}
      label={state}
      size="small"
    />
  );
};

interface PanelProps {
  testId: string;
  summary: React.ReactNode;
  children: React.ReactNode;
}

const Panel: React.FC<PanelProps> = ({ testId, summary, children }) => {
  const classes = useStyles();
  return (
    <ExpansionPanel className={classes.panel} data-testid={testId}>
      <ExpansionPanelSummary
        id={`${testId}-summary`}
        aria-controls={`${testId}-details`}
        className={classes.summary}
      >
        {summary}
      </ExpansionPanelSummary>
      <ExpansionPanelDetails id={`${testId}-details`} className={classes.panelDetails}>
        {children}
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
};

interface OpenInAwsChipProps {
  url: string;
  accountId?: string;
  label?: string;
}

const OpenInAwsChip: React.FC<OpenInAwsChipProps> = ({ url, accountId, label }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const tooltip = accountId
    ? `${t('tenantInventory.openInAwsTooltipPrefix')} ${accountId}.`
    : t('tenantInventory.openInAwsTooltipNoAccount');
  return (
    <Tooltip title={tooltip}>
      <Chip
        className={classes.awsChip}
        label={label ?? t('tenantInventory.openInAws')}
        icon={<OpenInNewWindow />}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          window.open(url, '_blank', 'noopener noreferrer');
        }}
        data-testid="inventory-aws-link"
      />
    </Tooltip>
  );
};

const AsgPanel = ({ asg, accountId }: { asg: InventoryAsg; accountId?: string }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const label = asg.Name || asg.AutoScalingGroupName;
  return (
    <Panel
      testId={`inventory-asg-${asg.AutoScalingGroupName}`}
      summary={
        <div className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>{label}</Typography>
          <OpenInAwsChip url={asgUrl(asg.Region, asg.AutoScalingGroupName)} accountId={accountId} />
        </div>
      }
    >
      <TenantInformationItem
        title={t('tenantInventory.asg.name')}
        value={asg.AutoScalingGroupName}
      />
      <TenantInformationItem title={t('tenantInventory.asg.minSize')} value={String(asg.MinSize)} />
      <TenantInformationItem title={t('tenantInventory.asg.maxSize')} value={String(asg.MaxSize)} />
      <TenantInformationItem
        title={t('tenantInventory.asg.desiredCapacity')}
        value={String(asg.DesiredCapacity)}
      />
      <TenantInformationItem
        title={t('tenantInventory.asg.healthCheckType')}
        value={asg.HealthCheckType}
      />
      <TenantInformationItem
        title={t('tenantInventory.asg.healthCheckGracePeriod')}
        value={
          asg.HealthCheckGracePeriod !== undefined ? String(asg.HealthCheckGracePeriod) : undefined
        }
      />
      <TenantInformationItem
        title={t('tenantInventory.asg.defaultCooldown')}
        value={asg.DefaultCooldown !== undefined ? String(asg.DefaultCooldown) : undefined}
      />
      <TenantInformationItem
        title={t('tenantInventory.common.availabilityZones')}
        value={asg.AvailabilityZones?.length ? asg.AvailabilityZones.join(', ') : undefined}
      />
    </Panel>
  );
};

const Ec2Panel = ({
  instance,
  testIdPrefix,
  accountId,
}: {
  instance: InventoryEc2Instance;
  testIdPrefix: string;
  accountId?: string;
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <Panel
      testId={`${testIdPrefix}-${instance.InstanceId}`}
      summary={
        <div className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>{instance.InstanceId}</Typography>
          <Chip className={classes.typeChip} label={instance.InstanceType} size="small" />
          <StateChip state={instance.State} />
          <OpenInAwsChip
            url={ec2InstanceUrl(instance.Region, instance.InstanceId)}
            accountId={accountId}
          />
        </div>
      }
    >
      <TenantInformationItem title={t('tenantInventory.ec2.id')} value={instance.InstanceId} />
      {instance.Name && (
        <TenantInformationItem title={t('tenantInventory.ec2.name')} value={instance.Name} />
      )}
      <TenantInformationItem title={t('tenantInventory.ec2.type')} value={instance.InstanceType} />
      <TenantInformationItem title={t('tenantInventory.ec2.state')} value={instance.State} />
      {instance.StateTransitionReason && (
        <TenantInformationItem
          title={t('tenantInventory.ec2.stateTransitionReason')}
          value={instance.StateTransitionReason}
        />
      )}
      <TenantInformationItem
        title={t('tenantInventory.ec2.ip')}
        value={instance.PrivateIpAddress}
      />
      <TenantInformationItem
        title={t('tenantInventory.common.availabilityZone')}
        value={instance.AvailabilityZone}
      />
      <TenantInformationItem title={t('tenantInventory.ec2.vpc')} value={instance.VpcId} />
      <TenantInformationItem title={t('tenantInventory.ec2.ami')} value={instance.ImageId} />
      <TenantInformationItem title={t('tenantInventory.ec2.key')} value={instance.KeyName} />
    </Panel>
  );
};

function groupInstancesByName(
  instances: InventoryEc2Instance[],
): { name: string; items: InventoryEc2Instance[] }[] {
  const groups = new Map<string, InventoryEc2Instance[]>();
  instances.forEach((inst) => {
    const key = inst.Name || inst.InstanceId;
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(inst);
    } else {
      groups.set(key, [inst]);
    }
  });
  return Array.from(groups.entries()).map(([name, items]) => ({ name, items }));
}

const InstanceGroup = ({
  name,
  instances,
  accountId,
}: {
  name: string;
  instances: InventoryEc2Instance[];
  accountId?: string;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.instanceGroup} data-testid={`inventory-instance-group-${name}`}>
      <div className={classes.instanceGroupHeader}>
        <Typography className={classes.instanceGroupName}>{name}</Typography>
        {instances.length > 1 && (
          <Chip className={classes.sectionCountChip} label={instances.length} size="small" />
        )}
      </div>
      {instances.map((inst) => (
        <Ec2Panel
          key={inst.InstanceId}
          instance={inst}
          testIdPrefix="inventory-ec2"
          accountId={accountId}
        />
      ))}
    </div>
  );
};

const RdsPanel = ({
  db,
  accountId,
  showRoleChip,
}: {
  db: InventoryRdsInstance;
  accountId?: string;
  showRoleChip: boolean;
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const summaryLabel = db.IsShared
    ? `${db.DBInstanceIdentifier} (${t('tenantInventory.rds.shared')})`
    : db.DBInstanceIdentifier;
  return (
    <Panel
      testId={`inventory-rds-${db.DBInstanceIdentifier}`}
      summary={
        <div className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>{summaryLabel}</Typography>
          {showRoleChip && db.Role && (
            <Chip
              className={`${classes.roleChip} ${
                db.Role === 'replica' ? classes.roleChipReplica : classes.roleChipPrimary
              }`}
              label={db.Role}
              size="small"
            />
          )}
          <StateChip state={db.DBInstanceStatus} />
          <OpenInAwsChip url={rdsUrl(db.Region, db.DBInstanceIdentifier)} accountId={accountId} />
        </div>
      }
    >
      <TenantInformationItem
        title={t('tenantInventory.rds.identifier')}
        value={db.DBInstanceIdentifier}
      />
      <TenantInformationItem
        title={t('tenantInventory.rds.instanceClass')}
        value={db.DBInstanceClass}
      />
      <TenantInformationItem
        title={t('tenantInventory.rds.engine')}
        value={db.EngineVersion ? `${db.Engine} ${db.EngineVersion}` : db.Engine}
      />
      <TenantInformationItem title={t('tenantInventory.rds.status')} value={db.DBInstanceStatus} />
      <TenantInformationItem
        title={t('tenantInventory.rds.endpoint')}
        value={
          db.EndpointAddress
            ? `${db.EndpointAddress}${db.EndpointPort ? `:${db.EndpointPort}` : ''}`
            : undefined
        }
      />
      <TenantInformationItem
        title={t('tenantInventory.rds.multiAz')}
        value={db.MultiAZ !== undefined ? String(db.MultiAZ) : undefined}
      />
      <TenantInformationItem
        title={t('tenantInventory.common.availabilityZone')}
        value={db.AvailabilityZone}
      />
      <TenantInformationItem
        title={t('tenantInventory.rds.allocatedStorage')}
        value={db.AllocatedStorage !== undefined ? `${db.AllocatedStorage} GB` : undefined}
      />
      {db.Iops !== undefined && (
        <TenantInformationItem title={t('tenantInventory.rds.iops')} value={`${db.Iops} IOPS`} />
      )}
      {db.StorageType && (
        <TenantInformationItem
          title={t('tenantInventory.rds.storageType')}
          value={db.StorageType}
        />
      )}
      {db.ReadReplicaSourceDBInstanceIdentifier && (
        <TenantInformationItem
          title={t('tenantInventory.rds.replicaSource')}
          value={db.ReadReplicaSourceDBInstanceIdentifier}
        />
      )}
    </Panel>
  );
};

export const TenantInventory: React.FC = () => {
  const { tenant } = useTenantContext();
  const { learnInstanceId } = useLearnInstance();
  const { site, loading: siteLoading } = useLearnSiteContext();
  const tenantId = tenant?.tenantType === 'Learn' ? learnInstanceId : tenant?.id;

  if (siteLoading || !site) {
    return <LoadingIndicator data-testid="tenant-inventory-loading" />;
  }
  const allowedStatuses = ['Installed', 'InMigration'];
  if (!allowedStatuses.includes(site.Status ?? '')) {
    return <NotAvailableBanner status={site.Status ?? 'Unknown'} />;
  }

  return <TenantInventoryInstalled tenantId={tenantId} />;
};

const NotAvailableBanner: React.FC<{ status: string }> = ({ status }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <div className={classes.unavailableWrapper}>
      <div className={classes.unavailableCard} data-testid="tenant-inventory-not-installed">
        <Alert className={classes.unavailableIcon} />
        <Typography variant="h3" className={classes.unavailableTitle}>
          {t('tenantInventory.notInstalledTitle')}
        </Typography>
        <div className={classes.unavailableStatusRow}>
          <Typography variant="body2" color="textSecondary">
            {t('tenantInventory.notInstalledCurrentStatus')}
          </Typography>
          <StateChip state={status} />
        </div>
        <Typography variant="body2" color="textSecondary">
          {t('tenantInventory.notInstalledExplain')}
        </Typography>
        <ul className={classes.unavailableHelpList}>
          <li>
            <Typography variant="body2">
              {t('tenantInventory.notInstalledAllowedInstalled')}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              {t('tenantInventory.notInstalledAllowedInMigration')}
            </Typography>
          </li>
        </ul>
      </div>
    </div>
  );
};

const TenantInventoryInstalled: React.FC<{ tenantId: string | undefined }> = ({ tenantId }) => {
  const { t } = useTranslation();
  const { data, loading, error, refresh } = useTenantInventory(tenantId);

  if (error) {
    return (
      <ErrorMessage
        title={t('tenantInventory.loadError')}
        message={error.message}
        data-testid="tenant-inventory-error"
      />
    );
  }
  if (!data) {
    return <LoadingIndicator data-testid="tenant-inventory-loading" />;
  }

  return <TenantInventoryView data={data} loading={loading} refresh={refresh} />;
};

interface TenantInventoryViewProps {
  data: NonNullable<ReturnType<typeof useTenantInventory>['data']>;
  loading: boolean;
  refresh: () => void;
}

const TenantInventoryView: React.FC<TenantInventoryViewProps> = ({ data, loading, refresh }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const installers = React.useMemo(() => data.installer ?? [], [data.installer]);
  const accountId =
    data.AccountId ??
    data.asg[0]?.AccountId ??
    data.instance[0]?.AccountId ??
    installers[0]?.AccountId ??
    data.rds[0]?.AccountId;
  const region =
    data.Region ??
    data.asg[0]?.Region ??
    data.instance[0]?.Region ??
    installers[0]?.Region ??
    data.rds[0]?.Region;

  return (
    <div data-testid="tenant-inventory-page">
      <div className={classes.pageTitleRow}>
        <div className={classes.pageTitleLeft}>
          <Typography variant="h2">{t('tenantInventory.pageTitle')}</Typography>
          {accountId && (
            <Chip
              className={classes.accountChip}
              icon={<AwsLogo />}
              label={accountId}
              size="small"
              data-testid="tenant-inventory-account-chip"
            />
          )}
          {region && (
            <Chip
              className={classes.accountChip}
              icon={<AwsRegionIcon />}
              label={region}
              size="small"
              data-testid="tenant-inventory-region-chip"
            />
          )}
        </div>
        <RefreshButton onRefresh={refresh} loading={loading} label={t('tenantInventory.refresh')} />
      </div>

      <div
        className={`${classes.refreshBlurWrapper} ${loading ? classes.refreshBlurActive : ''}`}
        data-testid="tenant-inventory-sections"
      >
        <Section
          sectionKey="asg"
          title={t('tenantInventory.sections.asg')}
          emptyLabel={t('tenantInventory.empty.asg')}
          isEmpty={data.asg.length === 0}
          count={data.asg.length}
          icon={<AwsAsgIcon />}
        >
          {data.asg.map((asg) => (
            <AsgPanel key={asg.AutoScalingGroupName} asg={asg} accountId={accountId} />
          ))}
        </Section>

        {installers.length > 0 && (
          <Section
            sectionKey="installer"
            title={t('tenantInventory.sections.installer')}
            emptyLabel={t('tenantInventory.empty.installer')}
            isEmpty={false}
            count={installers.length}
            icon={<AwsInstallerIcon />}
          >
            {installers.map((instance) => (
              <Ec2Panel
                key={instance.InstanceId}
                instance={instance}
                testIdPrefix="inventory-installer"
                accountId={accountId}
              />
            ))}
          </Section>
        )}

        <Section
          sectionKey="instances"
          title={t('tenantInventory.sections.instances')}
          emptyLabel={t('tenantInventory.empty.instances')}
          isEmpty={data.instance.length === 0}
          count={data.instance.length}
          icon={<AwsEc2Icon />}
        >
          {groupInstancesByName(data.instance).map((group) => (
            <InstanceGroup
              key={group.name}
              name={group.name}
              instances={group.items}
              accountId={accountId}
            />
          ))}
        </Section>

        <Section
          sectionKey="rds"
          title={t('tenantInventory.sections.rds')}
          emptyLabel={t('tenantInventory.empty.rds')}
          isEmpty={data.rds.length === 0}
          count={data.rds.length}
          icon={<AwsRdsIcon />}
        >
          {(() => {
            const hasReplica = data.rds.some((db) => db.Role === 'replica');
            return data.rds.map((db) => (
              <RdsPanel
                key={db.DBInstanceIdentifier}
                db={db}
                accountId={accountId}
                showRoleChip={hasReplica}
              />
            ));
          })()}
        </Section>
      </div>
    </div>
  );
};

export default TenantInventory;
